import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import {
  ArrowLeft, LocateFixed, Loader2, MapPin, Search as SearchIcon,
  Layers, Satellite, Sparkles, X, Compass, Navigation2, WifiOff, Wifi, Database,
} from "lucide-react";
import { toast } from "sonner";
import Map, {
  listMapProviders, resolveMapProviderId, setPreferredMapProviderId,
} from "@/components/map/MapProvider";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cacheKeyFor, savePinSet, loadPinSet, loadMostRecentPinSet } from "@/lib/offlinePins";
import { rerankPois } from "@/lib/rerankPois";
import { RouteWeatherStrip } from "@/components/weather/WeatherWidgets";
import { saveRoutePack, listRoutePacks, deleteRoutePack, getRoutePack } from "@/lib/offlineStore";
import { useOnline } from "@/lib/useOnline";
import { CachedBadge } from "@/components/OfflineBanner";
import MapLayerSwitcher from "@/components/MapLayerSwitcher";
import PoiTagSheet from "@/components/map/PoiTagSheet";
import FloatingSlot from "@/components/FloatingSlot";
import { getCategoryConfig } from "@/lib/poiCategories";
import { useI18n } from "@/context/I18nContext";

const DEFAULT_CENTER = [19.076, 72.8777];
const PROVIDER_OPTIONS = [
  { id: "maplibre",            label: "Street",    icon: Layers },
  { id: "maplibre-satellite",  label: "Satellite", icon: Satellite },
  { id: "leaflet",             label: "Classic",   icon: Compass },
];

// Turn a saved-at timestamp into "45 s ago" / "3 min ago" / "1 h ago".
// Used by the CACHED chip on the map header.
function formatAge(ts) {
  if (!ts) return "just now";
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60)     return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)     return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24)     return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function kmBetween([lat1, lng1], [lat2, lng2]) {
  const r = Math.PI / 180;
  const a = Math.sin((lat2 - lat1) * r / 2) ** 2 +
    Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin((lng2 - lng1) * r / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(a));
}

export default function NearbyMap() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);            // [{group, items:[...]}]
  const [byId,    setById]    = useState({});            // id -> catalog item
  const [active,  setActive]  = useState(               // Set<id> — pins to show
    () => {
      // Accept both ?cats=a,b,c (multi) and ?category=slug (single, from Home
      // chip deep-links). Merged so /nearby?category=washroom&cats=hospital works.
      const fromCats = params.get("cats")?.split(",").filter(Boolean) || [];
      const single = params.get("category");
      const all = [...fromCats, ...(single ? [single] : [])];
      return new Set(all);
    },
  );
  const [pos,     setPos]     = useState(null);
  const [locating, setLocating] = useState(false);
  const [following, setFollowing] = useState(false);     // continuous watch
  const [watchId, setWatchId] = useState(null);
  const [q,       setQ]       = useState(params.get("q") || "");
  const [busy,    setBusy]    = useState(false);
  const [emptyState, setEmptyState] = useState(null);  // warming | upstream | offline | none
  const [reloadTick, setReloadTick] = useState(0);
  const [results, setResults] = useState([]);
  const [grouped, setGrouped] = useState(null);
  const [nlHint,  setNlHint]  = useState(null);          // {radius_km, veg_only}
  const [providerId, setProviderId] = useState(() => resolveMapProviderId());
  const [providerOpen, setProviderOpen] = useState(false);
  const [detail, setDetail] = useState(null);           // clicked pin
  const [drawer, setDrawer] = useState(false);          // mobile category drawer
  // Offline UX state
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true);
  const [source, setSource] = useState("live");         // "live" | "cached"
  const [cachedAt, setCachedAt] = useState(null);        // ms timestamp when cached data was saved
  const [tick, setTick] = useState(0);                   // for re-rendering "Cached X ago"
  // Waypoint Playground (visual demo · no real routing yet). Up to 3 stops
  // between A and B, i.e. A → S1 → S2 → S3 → B. Each is { id, lat, lng, label }.
  const [waypointMode, setWaypointMode] = useState(false);
  const [waypoints,    setWaypoints]    = useState([]);   // ordered list of stops
  // Personal Map v2 — real A→B routing corridor (iter 55)
  const [routeData,    setRouteData]    = useState(null); // { route, pois, corridor_km, categories }
  const [routeBusy,    setRouteBusy]    = useState(false);
  const [corridorKm,   setCorridorKm]   = useState(1.0);
  const [alongCats,    setAlongCats]    = useState(["washroom", "cafe"]);
  // Weather + Compass (iter 56)
  const [weather,      setWeather]      = useState(null);   // current at map center
  const [destWeather,  setDestWeather]  = useState(null);   // arrival-time forecast at B
  const [heading,      setHeading]      = useState(null);   // compass degrees (0 = N)
  const [compassOn,    setCompassOn]    = useState(false);
  // Route Weather Timeline (iter 57)
  const [routeTimeline, setRouteTimeline] = useState(null);
  // Phase 3 · POI tagging surface (long-press → PoiTagSheet). Coord is
  // the crosshair position; user-tagged POIs render as their own marker
  // layer so they show up immediately after save.
  const [tagPos,      setTagPos]      = useState(null);
  const [userPois,    setUserPois]    = useState([]);
  // Offline route packs (Slice 2)
  const [savedPacks,  setSavedPacks]  = useState([]);
  const [packName,    setPackName]    = useState("");
  const [savingPack,  setSavingPack]  = useState(false);
  const { online: netOnline } = useOnline();
  useSeo({
    title: "Personal Map · find2near",
    description: "Discover everything nearby — bathrooms, water, petrol, food, hotels, hospitals, jobs, live booths — on your Personal Map.",
    canonical: "/nearby",
  });

  // -- Load catalog once ---------------------------------------------------
  useEffect(() => {
    api.get("/nearby/catalog")
       .then(r => {
         setCatalog(r.data.groups || []);
         const m = {};
         (r.data.groups || []).forEach(g => g.items.forEach(i => { m[i.id] = i; }));
         setById(m);
       })
       .catch(() => { /* silent — Personal Map still renders without pins */ });
  }, []);

  // -- Geolocation ---------------------------------------------------------
  const locate = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setLocating(false); },
      (e) => { setLocating(false); toast.error(e.message || "Location denied"); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };
  useEffect(() => { locate(); /* eslint-disable-next-line */ }, []);

  const toggleFollow = () => {
    if (following) {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      setWatchId(null); setFollowing(false); toast.info("Live follow off");
      return;
    }
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) => toast.error(e.message || "Location error"),
      { enableHighAccuracy: true, maximumAge: 5000 },
    );
    setWatchId(id); setFollowing(true); toast.success("Live follow on");
  };
  useEffect(() => () => { if (watchId != null) navigator.geolocation.clearWatch(watchId); }, [watchId]);

  // -- Online/offline listeners + tick for "Cached X ago" ------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline  = () => { setIsOnline(true);  toast.success("Back online — refreshing pins…"); };
    const onOffline = () => { setIsOnline(false); toast.warning("You're offline — showing cached pins"); };
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    const t = setInterval(() => setTick(x => x + 1), 30_000);
    return () => {
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(t);
    };
  }, []);

  // Keep the map's local isOnline in sync with the shared useOnline hook
  // (which includes heartbeat probes — catches captive portals + weak Wi-Fi
  // that navigator.onLine misses).
  useEffect(() => { setIsOnline(netOnline); }, [netOnline]);


  // -- Weather (current location) — refresh whenever the user's position moves
  //    more than ~1 km. Caches on backend for 10 min so quick pans are cheap.
  //    Falls back to the map's default center if geolocation is denied so
  //    the chip still tells the user what the weather is at the visible area.
  useEffect(() => {
    const lat = pos?.lat ?? DEFAULT_CENTER[0];
    const lng = pos?.lng ?? DEFAULT_CENTER[1];
    let cancelled = false;
    api.get("/weather/current", { params: { lat, lng } })
       .then(r => { if (!cancelled) setWeather(r.data); })
       .catch(() => { if (!cancelled) setWeather(null); });
    return () => { cancelled = true; };
  }, [pos && Math.round(pos.lat * 100), pos && Math.round(pos.lng * 100)]); // eslint-disable-line

  // -- Destination-arrival forecast — only fetched once we have a route.
  //    Uses the OSRM ETA (in minutes) to pick the right forecast hour so
  //    the user sees weather AT the moment they will arrive at B.
  useEffect(() => {
    const path = routeData?.route?.coords;
    const eta  = routeData?.route?.duration_min ?? 0;
    if (!path || path.length < 2) { setDestWeather(null); return; }
    const [lat, lng] = path[path.length - 1];
    const hours = Math.max(1, Math.min(72, Math.ceil((eta + 5) / 60)));
    let cancelled = false;
    api.get("/weather/forecast", { params: { lat, lng, hours } })
       .then(r => {
         if (cancelled) return;
         const rows = r.data?.hourly || [];
         const idx  = Math.max(0, Math.min(rows.length - 1, Math.floor(eta / 60)));
         setDestWeather({ ...r.data, arrival: rows[idx], eta_min: eta });
       })
       .catch(() => { if (!cancelled) setDestWeather(null); });
    return () => { cancelled = true; };
  }, [routeData]);

  // -- Compass — subscribes to DeviceOrientation. iOS requires a permission
  //    prompt on first tap; Android just streams events. `heading` is degrees
  //    clockwise from magnetic north (0 = N, 90 = E, 180 = S, 270 = W).
  useEffect(() => {
    if (!compassOn || typeof window === "undefined") return;
    let unmounted = false;
    const handler = (e) => {
      if (unmounted) return;
      const h = (typeof e.webkitCompassHeading === "number")
        ? e.webkitCompassHeading
        : (typeof e.alpha === "number" ? (360 - e.alpha) % 360 : null);
      if (h != null) setHeading(h);
    };
    const attach = () => {
      window.addEventListener("deviceorientationabsolute", handler, true);
      window.addEventListener("deviceorientation", handler, true);
    };
    const DE = typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null;
    if (DE && typeof DE.requestPermission === "function") {
      DE.requestPermission().then(res => { if (res === "granted") attach(); }).catch(() => {});
    } else {
      attach();
    }
    return () => {
      unmounted = true;
      window.removeEventListener("deviceorientationabsolute", handler, true);
      window.removeEventListener("deviceorientation", handler, true);
    };
  }, [compassOn]);

  // -- Route Weather Timeline — fetch alongside the destination-arrival card
  //    so drivers see which stretch will be wet, not just the endpoint.
  useEffect(() => {
    const path = routeData?.route?.coords;
    const dur  = routeData?.route?.duration_min ?? 0;
    if (!path || path.length < 2) { setRouteTimeline(null); return; }
    let cancelled = false;
    const stride = Math.max(1, Math.ceil(path.length / 200));
    const trimmed = path.filter((_, i) => i % stride === 0 || i === path.length - 1);
    api.post("/weather/route-timeline", {
      coords: trimmed, duration_min: dur, samples: 5,
    }).then(r => { if (!cancelled) setRouteTimeline(r.data); })
      .catch(() => { if (!cancelled) setRouteTimeline(null); });
    return () => { cancelled = true; };
  }, [routeData]);


  // -- Fetch pins whenever position / category selection / online status
  //    changes. Uses offline cache as a safety net.
  useEffect(() => {
    if (!pos) return;
    setBusy(true);
    const cats = Array.from(active);
    const radius = nlHint?.radius_km || 3;
    const cKey = cacheKeyFor({ lat: pos.lat, lng: pos.lng, cats, radius });
    let cancelled = false;
    setEmptyState(null);
    (async () => {
      try {
        let flat = [];
        let usedCache = false;
        let upstreamBusy = false;
        if (cats.length === 0) {
          try {
            const params = { lat: pos.lat, lng: pos.lng, radius_km: radius, limit: 60 };
            let r = await api.get("/discover/nearby", { params });
            if (cancelled) return;                       // BUG-A3-15: a superseded "All" load must not overwrite state
            if (r.data?.warming) {
              // Server is still filling its cache for this area — ask once more shortly.
              setEmptyState("warming");
              await new Promise(res => setTimeout(res, (r.data.retry_after || 20) * 1000));
              if (cancelled) return;
              r = await api.get("/discover/nearby", { params });
            }
            const g = r.data?.grouped || {};
            if (r.data?.warming || r.data?.unavailable) upstreamBusy = true;
            setGrouped(g);
            flat = Object.entries(g).flatMap(([k, rows]) => (rows || []).map((x) => ({ category: k, ...x })));
          } catch { usedCache = true; }
        } else {
          try {
            const promises = cats.map(c => api.get("/discover/nearby", {
              params: { lat: pos.lat, lng: pos.lng, category: c, radius_km: radius, limit: 15 },
            }));
            const settled = await Promise.all(promises.map(p => p.catch(() => null)));
            const allOk = settled.every(r => r && r.data);
            if (!allOk) usedCache = true;
            if (settled.some(r => r?.data?.unavailable)) upstreamBusy = true;   // BUG-A3-05
            settled.forEach((r, i) => {
              const cat = cats[i];
              (r?.data?.results || []).forEach(row => flat.push({ ...row, category: cat }));
            });
            setGrouped(null);
          } catch { usedCache = true; }
        }

        if (cancelled) return;
        if (!usedCache && flat.length > 0) {
          setEmptyState(null);
          // Online success → save to cache and mark LIVE.
          setResults(flat);
          setSource("live");
          setCachedAt(null);
          savePinSet(cKey, {
            pins: flat, center: [pos.lat, pos.lng], radius, cats,
            meta: { grouped: cats.length === 0 },
          });
        } else {
          // Fetch failed OR empty → try cache. Prefer exact-key match; fall
          // back to the most recent set so travellers get *something*.
          const recent = await loadMostRecentPinSet();
          // Never show another town's pins as if they were here.
          const recentIsHere = recent?.center && kmBetween(recent.center, [pos.lat, pos.lng]) <= 2;
          const exact = await loadPinSet(cKey);
          const cached = exact || (recentIsHere ? recent : null);
          // BUG-A3-03: a fallback set from another query must still match the active category chips.
          const pins = !cached?.pins ? [] : exact || !cats.length ? cached.pins
            : cached.pins.filter((p) => cats.includes(p.category));
          if (cancelled) return;
          if (pins.length) {
            setResults(pins);
            setSource("cached");
            setCachedAt(cached.ts);
            setGrouped(null);
            if (usedCache) toast.info(`Showing cached pins from ${formatAge(cached.ts)}`);
          } else {
            // No cache either — say why the map is empty instead of showing a blank "live" map.
            setResults(flat);
            setSource(flat.length ? "live" : "cached");
            setCachedAt(null);
            if (!flat.length) setEmptyState(usedCache ? "offline" : upstreamBusy ? "upstream" : "none");
          }
        }
      } finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [pos?.lat, pos?.lng, Array.from(active).join("|"), nlHint?.radius_km, isOnline, reloadTick]);   // eslint-disable-line

  // -- Sync URL for shareable state ----------------------------------------
  // NOTE: preserves the deep-link params (layer/poi/action) so a
  // notification-originated URL is not silently overwritten when the
  // component mounts with an empty category selection.
  useEffect(() => {
    const next = new URLSearchParams();
    if (active.size) next.set("cats", Array.from(active).join(","));
    if (q)           next.set("q", q);
    for (const k of ["layer", "poi", "action"]) {
      const v = params.get(k);
      if (v) next.set(k, v);
    }
    setParams(next, { replace: true });
    // eslint-disable-next-line
  }, [Array.from(active).join(","), q]);

  // -- Waypoint Playground helpers -----------------------------------------
  // Colour + label per stop position. A = user origin (or first pin), then
  // S1 / S2 / S3, and the LAST pin is always B (destination).
  const waypointMarkers = useMemo(() => {
    if (!waypoints.length) return [];
    return waypoints.map((w, i) => {
      const isFirst = i === 0;
      const isLast  = i === waypoints.length - 1 && waypoints.length > 1;
      const label   = isFirst ? "A" : isLast ? "B" : `S${i}`;
      const color   = isFirst ? "#0ea5e9" : isLast ? "#dc2626" : "#f59e0b";
      return {
        key:   `wp_${w.id}`,
        lat:   w.lat, lng: w.lng,
        icon:  label,
        color, title: `Waypoint ${label}`,
        popup: `Waypoint ${label} (planning · no live routing yet)`,
      };
    });
  }, [waypoints]);
  const waypointPolyline = useMemo(() => {
    // If we have a real OSRM route, draw the solid path in emerald. Otherwise
    // fall back to the amber dashed line between the raw waypoints.
    if (routeData?.route?.coords?.length >= 2) {
      return [{
        id:    "route-v2",
        path:  routeData.route.coords,
        color: routeData.route.provider === "stub" ? "#f59e0b" : "#10b981",
        width: 5,
        dashed: routeData.route.provider === "stub",
      }];
    }
    if (waypoints.length < 2) return [];
    return [{
      id:    "playground",
      path:  waypoints.map(w => [w.lat, w.lng]),
      color: "#f59e0b",
      width: 4,
      dashed: true,
    }];
  }, [waypoints, routeData]);
  // POIs discovered along the corridor become extra markers on the map.
  const alongRouteMarkers = useMemo(() => {
    const pois = routeData?.pois || [];
    return pois.map((p, i) => ({
      key:   `along_${p.listing_id || i}`,
      lat:   p.lat, lng: p.lng,
      icon:  (byId[p.category]?.icon) || "•",
      color: byId[p.category]?.color || "#0f766e",
      title: p.title,
      popup: `${p.title} · ${(p.category || "").replace(/_/g, " ")} · +${p.detour_min}min detour`,
      category: p.category,
      distance_km: p.dist_from_route_km,
      along_route: true,
    }));
  }, [routeData, byId]);

  // Bearing (degrees clockwise from N) from the user's current position to
  // the last waypoint / route endpoint. Used by the AR-style compass arrow
  // to point at the *next stop*, not just true north.
  const targetBearing = useMemo(() => {
    if (!pos) return null;
    const routeEnd = routeData?.route?.coords?.length
      ? routeData.route.coords[routeData.route.coords.length - 1]
      : null;
    const wpEnd = waypoints.length
      ? [waypoints[waypoints.length - 1].lat, waypoints[waypoints.length - 1].lng]
      : null;
    const target = routeEnd || wpEnd;
    if (!target) return null;
    const toRad = (d) => d * Math.PI / 180;
    const toDeg = (r) => r * 180 / Math.PI;
    const φ1 = toRad(pos.lat), φ2 = toRad(target[0]);
    const λ1 = toRad(pos.lng), λ2 = toRad(target[1]);
    const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
  }, [pos, routeData, waypoints]);
  // Rotation for the AR arrow — offset target bearing by the phone's heading
  // so "up" on the arrow always points to the physical direction of the stop.
  const arArrowRotation = (typeof targetBearing === "number" && typeof heading === "number")
    ? ((targetBearing - heading + 360) % 360) : null;

  // Fetch the real corridor route + POIs from the backend.
  const searchAlongRoute = async () => {
    if (waypoints.length < 2) {
      toast.warning("Drop at least an A and B pin first.");
      return;
    }
    setRouteBusy(true);
    try {
      const body = {
        origin:      { lat: waypoints[0].lat, lng: waypoints[0].lng },
        destination: { lat: waypoints[waypoints.length - 1].lat, lng: waypoints[waypoints.length - 1].lng },
        waypoints:   waypoints.slice(1, -1).map(w => ({ lat: w.lat, lng: w.lng })),
        categories:  alongCats,
        corridor_km: corridorKm,
        total_limit: 40,
      };
      const r = await api.post("/discover/along-route", body);
      setRouteData(r.data);
      const km  = r.data?.route?.distance_km ?? 0;
      const min = r.data?.route?.duration_min ?? 0;
      const prov = r.data?.route?.provider === "stub" ? " · straight-line fallback" : "";
      toast.success(`Route: ${km} km · ${min} min · ${r.data?.count || 0} POIs${prov}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Along-route search failed");
    } finally {
      setRouteBusy(false);
    }
  };
  const clearRoute = () => setRouteData(null);

  // ── Map layers (P2 + P2b) — POI layers + traffic feature-flag ───────
  // `layerPins` is fetched from `/api/map/pois` per active layer and
  // rendered as MapLibre markers alongside the category chip results.
  // Auto-refetches when the viewport bounds change (moveend) or when
  // the active-layer set changes — never re-fetches on every render.
  const [activeLayers, setActiveLayers]   = useState([]);
  const [layerPins,    setLayerPins]      = useState([]);
  const [layerBusy,    setLayerBusy]      = useState(false);
  const [layerEmpty,   setLayerEmpty]     = useState(null);  // {layer,label} or null
  const [trafficEnabled, setTrafficEnabled] = useState(false);
  const [trafficActive,  setTrafficActive]  = useState(false);
  const bboxRef = React.useRef(null);   // last known viewport bounds
  useEffect(() => {
    api.get("/map/traffic-config").then(r => setTrafficEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);

  const fetchLayerPois = React.useCallback(async (layerIds, bbox) => {
    if (!layerIds.length) { setLayerPins([]); setLayerEmpty(null); return; }
    // Fall back to a ~15km square around `pos` if the map hasn't emitted
    // its first `moveend` bounds yet.
    let { south, west, north, east } = bbox || {};
    if ([south, west, north, east].some((v) => typeof v !== "number")) {
      if (pos?.lat != null) {
        south = pos.lat - 0.15; north = pos.lat + 0.15;
        west  = pos.lng - 0.15; east  = pos.lng + 0.15;
      } else {
        south = 19.0; north = 19.3; west = 72.8; east = 73.0;
      }
    }
    setLayerBusy(true);
    const all = [];
    const emptyLayers = [];
    try {
      const results = await Promise.all(layerIds.map(id =>
        api.get("/map/pois", { params: { layer: id, south, west, north, east, limit: 60 } })
          .then(r => ({ id, items: r.data?.items || [] }))
          .catch(() => ({ id, items: [] }))
      ));
      for (const { id, items } of results) {
        if (!items.length) emptyLayers.push(id);
        all.push(...items);
      }
    } finally { setLayerBusy(false); }
    setLayerPins(all);
    if (all.length === 0 && layerIds.length) {
      setLayerEmpty({ layers: layerIds });
    } else {
      setLayerEmpty(null);
    }
  }, [pos]);

  const toggleLayer = (id, on) => {
    setActiveLayers(prev => {
      const next = on ? [...new Set([...prev, id])] : prev.filter(x => x !== id);
      fetchLayerPois(next, bboxRef.current);
      return next;
    });
  };

  // ── Marker deep-link (P3) — /nearby?layer=<id>&poi=<canonical-id> ─────
  // Opens the map with the layer active, focuses on the exact POI, and
  // pops the detail sheet. Falls back gracefully when the POI no longer
  // exists so a stale notification never leaves the user stranded.
  const deepLinkLayer = params.get("layer");
  const deepLinkPoi   = params.get("poi");
  const deepLinkAction = params.get("action") || "highlight";
  const deepLinkAppliedRef = React.useRef(false);
  useEffect(() => {
    if (!deepLinkLayer || deepLinkAppliedRef.current) return;
    deepLinkAppliedRef.current = true;
    // Enable the layer if not already active. `fetchLayerPois` is
    // called through toggleLayer so the viewport POIs load next tick.
    toggleLayer(deepLinkLayer, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkLayer]);

  // After POIs load, locate the requested POI. If missing → toast the
  // graceful "no longer available" message.
  const deepLinkResolvedRef = React.useRef(false);
  useEffect(() => {
    if (!deepLinkPoi || !layerPins.length || deepLinkResolvedRef.current) return;
    const match = layerPins.find(p => p.id === deepLinkPoi
                                     || p.external_id === deepLinkPoi);
    if (match) {
      deepLinkResolvedRef.current = true;
      // Build the same shape the marker click produces so the detail
      // sheet renders consistently.
      const cfg = getCategoryConfig(match.category);
      setDetail({
        key: match.id, lat: match.lat, lng: match.lng,
        icon: "•", color: cfg.color,
        category: match.category, layer_id: match.category,
        title: match.name, popup: match.name,
        address: match.address, phone: match.phone,
        website: match.website, source: match.source,
        raw: match,
      });
      if (deepLinkAction === "highlight" || deepLinkAction === "open") {
        setPos({ lat: match.lat, lng: match.lng });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkPoi, layerPins]);

  // After the first non-empty fetch settles, if we STILL couldn't find
  // the requested POI, tell the user gracefully.
  useEffect(() => {
    if (!deepLinkPoi || deepLinkResolvedRef.current || layerBusy) return;
    // Only fire once the fetch has actually completed at least once.
    if (layerPins.length === 0 && !layerEmpty) return;
    const found = layerPins.some(p => p.id === deepLinkPoi
                                    || p.external_id === deepLinkPoi);
    if (!found) {
      deepLinkResolvedRef.current = true;
      toast.info(t("map.poi.gone", "This place is no longer available."));
    }
  }, [deepLinkPoi, layerPins, layerBusy, layerEmpty, t]);

  // Re-fetch when the map viewport changes so POIs match what the user
  // is actually looking at. Debounced by MapLibre's moveend coalescing.
  const onMapBoundsChange = React.useCallback((b) => {
    bboxRef.current = b;
    if (activeLayers.length) fetchLayerPois(activeLayers, b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayers, fetchLayerPois]);

  // ── Offline Slice 2 — Route pack persistence ─────────────────────────
  const reloadPacks = async () => setSavedPacks(await listRoutePacks());
  useEffect(() => { reloadPacks(); }, []);

  // Deep-link handoff from /settings/offline: `state: { loadPack: <id> }`.
  const routerLoc = useLocation();
  useEffect(() => {
    const packId = routerLoc.state?.loadPack;
    if (packId) loadPack(packId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.key]);

  const saveCurrentPack = async () => {
    if (!routeData?.route?.coords?.length) {
      toast.warning("Run a corridor search first — then save it for offline use.");
      return;
    }
    setSavingPack(true);
    try {
      const first = waypoints[0], last = waypoints[waypoints.length - 1];
      const label = packName.trim()
        || `Route · ${new Date().toLocaleDateString()} · ${routeData.route.distance_km} km`;
      await saveRoutePack({
        pack_id:     `pk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name:        label,
        origin:      first ? { lat: first.lat, lng: first.lng } : null,
        destination: last  ? { lat: last.lat,  lng: last.lng  } : null,
        waypoints:   waypoints.slice(1, -1),
        categories:  alongCats,
        corridor_km: corridorKm,
        route:       routeData.route,
        pois:        routeData.pois || [],
        weather_timeline: routeTimeline,
        size_estimate: JSON.stringify(routeData).length,
      });
      setPackName("");
      await reloadPacks();
      toast.success(`Saved "${label}" for offline use.`);
    } catch (err) {
      toast.error(err?.message || "Could not save route pack.");
    } finally { setSavingPack(false); }
  };

  const loadPack = async (packId) => {
    const pack = await getRoutePack(packId);
    if (!pack) { toast.error("Route pack not found."); return; }
    // Restore state — waypoints + route data + POIs.
    const wp = [
      ...(pack.origin ? [pack.origin] : []),
      ...(pack.waypoints || []),
      ...(pack.destination ? [pack.destination] : []),
    ];
    setWaypoints(wp);
    setAlongCats(pack.categories || []);
    setCorridorKm(pack.corridor_km || 1.0);
    setRouteData({ route: pack.route, pois: pack.pois, count: (pack.pois || []).length,
                   categories: pack.categories || [], corridor_km: pack.corridor_km });
    setRouteTimeline(pack.weather_timeline || null);
    // Also hydrate the map's main results list so the LIVE/CACHED chip lights
    // up (this is what the chip visibility hinges on).
    if (Array.isArray(pack.pois) && pack.pois.length) {
      setResults(pack.pois);
      setSource("cached");
      setCachedAt(pack.saved_at || Date.now());
    }
    setWaypointMode(true);
    toast.success(`Loaded "${pack.name}" ${netOnline ? "(live data will refresh)" : "(offline mode)"}`);
  };

  const removePack = async (packId) => {
    await deleteRoutePack(packId);
    await reloadPacks();
    toast.success("Route pack removed.");
  };
  const handleMapClickForWaypoint = (coord) => {
    // Cap: A → S1 → S2 → B = 4 pins total (up to 2 intermediate stops).
    // Matches the "A → Stop 1 → Stop 2 → B" example in the CORE REQUIREMENT.
    if (waypoints.length >= 4) {
      toast.warning("Playground supports up to 4 pins: A → S1 → S2 → B.");
      return;
    }
    setWaypoints(list => [...list, {
      id: `wp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      lat: coord.lat, lng: coord.lng,
    }]);
  };
  const removeWaypoint = (id) => setWaypoints(list => list.filter(w => w.id !== id));
  const moveWaypoint   = (idx, delta) => setWaypoints(list => {
    const j = idx + delta;
    if (j < 0 || j >= list.length) return list;
    const out = list.slice();
    [out[idx], out[j]] = [out[j], out[idx]];
    return out;
  });
  const clearWaypoints = () => setWaypoints([]);
  const toggleWaypointMode = () => {
    setWaypointMode(m => {
      const next = !m;
      if (next) toast.info("Tap the map to drop pins: A → stops → B, then run corridor search.");
      return next;
    });
  };


  const rankedResults = useMemo(
    () => rerankPois(results, q, nlHint),
    [results, q, nlHint?.veg_only, nlHint?.price_bias, nlHint?.brand],
  );

  // -- Marker set for the map ---------------------------------------------
  const markers = useMemo(() => {
    const arr = [];
    if (pos) arr.push({
      key: "me", lat: pos.lat, lng: pos.lng,
      icon: "★", color: "#0ea5e9", pulse: true, title: "You",
    });
    (rankedResults || []).slice(0, 200).forEach((r, i) => {
      const lat = r.lat ?? r.latitude, lng = r.lng ?? r.longitude;
      if (typeof lat !== "number" || typeof lng !== "number") return;
      const meta = byId[r.category] || {};
      arr.push({
        key:      r.id || `p_${i}`,
        lat, lng,
        icon:     meta.icon || "•",
        color:    meta.color || "#0f766e",
        category: r.category,
        title:    r.name || r.title || meta.label || "Place",
        popup:    r.name || r.title || meta.label,
        address:  r.address, distance_km: r.distance_km,
        raw: r,
      });
    });
    // P2b · POI layer markers — real data from `/api/map/pois` (listings
    // first, then OSM fallback). Each pin carries a `layer_id` so the
    // detail sheet can render the correct lucide icon + action buttons.
    (layerPins || []).forEach((p, i) => {
      if (typeof p.lat !== "number" || typeof p.lng !== "number") return;
      const cfg = getCategoryConfig(p.category);
      arr.push({
        key:       p.id || `lp_${i}`,
        lat:       p.lat, lng: p.lng,
        icon:      "•",                // colored teardrop; lucide shown in the detail sheet
        color:     cfg.color,
        category:  p.category,
        layer_id:  p.category,
        title:     p.name || cfg.label,
        popup:     p.name || cfg.label,
        address:   p.address,
        phone:     p.phone,
        website:   p.website,
        source:    p.source,
        raw:       p,
      });
    });
    return arr;
  }, [pos, rankedResults, byId, layerPins]);

  const center = pos ? [pos.lat, pos.lng] : DEFAULT_CENTER;

  // -- Natural-language search --------------------------------------------
  const runNL = async () => {
    const query = q.trim();
    if (!query) return;
    try {
      const r = await api.post("/nearby/nl-search", { q: query, lat: pos?.lat, lng: pos?.lng });
      const cats = r.data?.categories || [];
      if (cats.length) {
        setActive(new Set(cats));
        setNlHint({ radius_km: r.data.radius_km, veg_only: r.data.veg_only });
        toast.success(`Showing ${cats.length} categor${cats.length === 1 ? "y" : "ies"}${r.data.radius_km !== 3 ? ` within ${r.data.radius_km} km` : ""}`);
      } else {
        toast.info("Try a different phrase like 'bathroom near me' or 'petrol within 2 km'");
      }
    } catch { toast.error("Search failed"); }
  };

  const toggleCat = (id) => {
    setActive(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const pickProvider = (id) => {
    setPreferredMapProviderId(id);
    setProviderId(id);
    setProviderOpen(false);
  };

  return (
    <div className="h-screen flex flex-col bg-stone-50" data-testid="nearby-page">
      <MapLayerSwitcher active={activeLayers} onToggle={toggleLayer}
                        trafficEnabled={trafficEnabled}
                        trafficActive={trafficActive}
                        onToggleTraffic={setTrafficActive}/>
      {/* Sticky search bar */}
      <div className="px-3 md:px-6 pt-2 pb-2 flex items-center gap-2 border-b border-stone-200 bg-white z-30 sticky top-0">
        <Link to="/" className="text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 text-xs" data-testid="nearby-back"><ArrowLeft size={13}/></Link>
        <div className="flex-1 relative">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && runNL()}
                 placeholder='Try "washroom near me" or "vegetarian food within 2 km"'
                 className="pl-9 pr-20 rounded-full h-9 text-sm" data-testid="nearby-search-input"/>
          <Button onClick={runNL}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[10px] font-black px-2"
                  data-testid="nearby-nl-btn"><Sparkles size={11} className="mr-1"/> Ask</Button>
        </div>
        <Link to={`/search?q=${encodeURIComponent(q)}`} className="text-[11px] font-bold text-sky-700 underline whitespace-nowrap" data-testid="nearby-search-everywhere">Search all</Link>
        <Button onClick={locate} disabled={locating}
                className="rounded-full bg-stone-900 hover:bg-black text-white text-[11px] font-black h-9 px-3"
                data-testid="nearby-locate-btn">
          {locating ? <Loader2 size={12} className="animate-spin"/> : <LocateFixed size={12}/>}
        </Button>
      </div>

      {/* Category chips */}
      <div className="px-3 md:px-6 py-2 overflow-x-auto whitespace-nowrap border-b border-stone-200 bg-white z-20" data-testid="nearby-chip-row">
        <button onClick={() => setActive(new Set())}
                className={`inline-flex items-center rounded-full text-[11px] font-black px-3 py-1.5 mr-1 ${active.size === 0 ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}
                data-testid="nearby-chip-all">All</button>
        {catalog.flatMap(g => g.items).slice(0, 30).map((c) => {
          const on = active.has(c.id);
          return (
            <button key={c.id} onClick={() => toggleCat(c.id)}
                    className={`inline-flex items-center rounded-full text-[11px] font-black px-3 py-1.5 mr-1 gap-1 ${on ? "text-white" : "bg-stone-100 text-stone-700"}`}
                    style={on ? { backgroundColor: c.color } : {}}
                    data-testid={`nearby-chip-${c.id}`}>
              <span>{c.icon}</span> {c.label}
            </button>
          );
        })}
        <button onClick={() => setDrawer(true)}
                className="inline-flex items-center rounded-full text-[11px] font-black px-3 py-1.5 mr-1 bg-emerald-50 text-emerald-700"
                data-testid="nearby-chip-more">
          + All categories ({catalog.reduce((n, g) => n + g.items.length, 0)})
        </button>
      </div>

      {/* Split map + list */}
      <div className="flex-1 grid md:grid-cols-[1fr_360px] min-h-0">
        <div className="relative min-h-[45vh]" data-testid="nearby-map-wrap">
          <Map provider={providerId} center={center} zoom={pos ? 15 : 12}
               markers={waypointMarkers.concat(alongRouteMarkers).concat(markers).concat(
                 userPois.map((p, i) => ({
                   key: `up_${p.place_id || i}`,
                   lat: p.lat, lng: p.lng,
                   icon: "📍", color: "#059669",
                   title: p.name || p.category,
                   popup: p.name || p.category,
                   category: p.category,
                 })).concat(tagPos ? [{
                   key: "tag-crosshair", lat: tagPos.lat, lng: tagPos.lng,
                   icon: "✚", color: "#059669", pulse: true, title: "New place",
                 }] : []),
               )}
               polylines={waypointPolyline}
               height="100%"
               onMarkerClick={(m) => { if (!waypointMode) setDetail(m); }}
               onMapClick={waypointMode ? handleMapClickForWaypoint : undefined}
               onBoundsChange={onMapBoundsChange}
               onLongPress={(c) => setTagPos(c)}/>

          {tagPos && (
            <PoiTagSheet
              position={tagPos}
              onClose={() => setTagPos(null)}
              onAdjust={(c) => setTagPos(c)}
              onSaved={(place) => {
                if (place) setUserPois((list) => [...list, place]);
              }}
            />
          )}

          {/* Provider picker */}
          <div className="absolute top-3 left-3 z-[500]">
            <button onClick={() => setProviderOpen(o => !o)}
                    className="rounded-full bg-white/95 shadow border border-stone-200 px-3 py-1.5 text-[11px] font-black text-stone-700 inline-flex items-center gap-1"
                    data-testid="nearby-provider-btn">
              <Layers size={12}/> {PROVIDER_OPTIONS.find(p => p.id === providerId)?.label || "Map"}
            </button>
            {providerOpen && (
              <div className="mt-2 rounded-2xl bg-white shadow-lg border border-stone-200 p-2 w-52" data-testid="nearby-provider-menu">
                {PROVIDER_OPTIONS.map((p) => {
                  const Icon = p.icon;
                  const on = providerId === p.id;
                  return (
                    <button key={p.id} onClick={() => pickProvider(p.id)}
                            className={`w-full text-left flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12px] font-black ${on ? "bg-emerald-100 text-emerald-800" : "text-stone-700 hover:bg-stone-100"}`}
                            data-testid={`nearby-provider-${p.id}`}>
                      <Icon size={13}/> {p.label}
                      {on && <span className="ml-auto text-[10px]">✓</span>}
                    </button>
                  );
                })}
                <div className="text-[10px] text-stone-500 mt-1 px-1">
                  Available providers: {listMapProviders().length}
                </div>
                <Link to="/settings/storage"
                      className="mt-2 flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] font-black text-emerald-700 hover:bg-emerald-50 border-t border-stone-100 pt-2"
                      data-testid="nearby-storage-link"
                      onClick={() => setProviderOpen(false)}>
                  <Database size={12}/> Manage cached maps
                </Link>
              </div>
            )}
          </div>

          {/* Follow-me toggle */}
          <button onClick={toggleFollow}
                  className={`absolute bottom-4 right-4 z-[500] rounded-full shadow-lg border-2 border-white h-11 w-11 grid place-items-center ${following ? "bg-emerald-600 text-white" : "bg-white text-stone-700"}`}
                  data-testid="nearby-follow-btn"
                  aria-label="Toggle live location follow">
            <Navigation2 size={16}/>
          </button>

          {/* Weather chip (top-right) — live conditions at map center.
              Falls back to a "—" state if Open-Meteo is unreachable. */}
          {weather?.available && (
            <div className="absolute top-3 right-3 z-[500] rounded-2xl bg-white/95 shadow border border-stone-200 px-3 py-1.5 text-[11px] font-black text-stone-700 flex items-center gap-2"
                 data-testid="nearby-weather-chip"
                 title={`${weather.condition?.label} · humidity ${weather.humidity_pct}% · wind ${weather.wind_kph} kph · UV ${weather.uv_index}`}>
              <span className="text-[15px]" aria-hidden>
                {weather.condition?.icon === "sun" ? "☀️"
                 : weather.condition?.icon === "cloud-sun" ? "⛅"
                 : weather.condition?.icon?.startsWith("cloud-rain") ? "🌧️"
                 : weather.condition?.icon === "cloud-showers-heavy" ? "⛈️"
                 : weather.condition?.icon === "bolt" ? "⚡"
                 : weather.condition?.icon === "snowflake" ? "❄️"
                 : weather.condition?.icon === "cloud-fog" ? "🌫️"
                 : weather.condition?.icon === "cloud-drizzle" ? "🌦️"
                 : "☁️"}
              </span>
              <span data-testid="weather-temp">{Math.round(weather.temp_c ?? 0)}°C</span>
              <span className="text-stone-400 hidden sm:inline">·</span>
              <span className="hidden sm:inline text-stone-500 font-medium">{weather.condition?.label}</span>
            </div>
          )}

          {/* Compass overlay (bottom-left) — tap to enable device orientation.
              When active, the outer ring rotates so N always points to true
              magnetic north; the arrow inside points to the top of the phone.
              When a route is active AND compass is on, an emerald AR arrow
              overlays the ring pointing at the next waypoint. */}
          <button onClick={() => setCompassOn(c => !c)}
                  className={`absolute bottom-4 left-4 z-[500] rounded-full shadow-lg border-2 border-white h-11 w-11 grid place-items-center ${compassOn ? "bg-sky-600 text-white" : "bg-white text-stone-700"}`}
                  data-testid="nearby-compass-btn"
                  aria-label={compassOn ? "Disable compass" : "Enable compass"}
                  title={compassOn ? (heading != null ? `Heading: ${Math.round(heading)}°` : "Waiting for sensor…") : "Enable device compass"}>
            <div className="relative w-7 h-7">
              <div className="absolute inset-0 rounded-full border-2 border-current transition-transform duration-200"
                   style={{ transform: `rotate(${compassOn && heading != null ? -heading : 0}deg)` }}
                   data-testid="compass-ring">
                <div className="absolute top-[-3px] left-1/2 -translate-x-1/2 text-[9px] font-black text-rose-500">N</div>
              </div>
              {compassOn && arArrowRotation != null ? (
                <div className="absolute inset-0 grid place-items-center transition-transform duration-200"
                     style={{ transform: `rotate(${arArrowRotation}deg)` }}
                     data-testid="compass-ar-arrow"
                     title={`Waypoint bearing ${Math.round(targetBearing)}°`}>
                  <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[12px] border-l-transparent border-r-transparent border-b-emerald-300"/>
                </div>
              ) : (
                <Compass size={14} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"/>
              )}
            </div>
          </button>
          {compassOn && heading != null && (
            <div className="absolute bottom-[70px] left-4 z-[500] rounded-full bg-slate-900/85 text-white text-[10px] font-mono px-2 py-0.5"
                 data-testid="compass-heading">
              {Math.round(heading)}° {(() => {
                const h = heading;
                if (h < 22.5 || h >= 337.5) return "N";
                if (h < 67.5)  return "NE";
                if (h < 112.5) return "E";
                if (h < 157.5) return "SE";
                if (h < 202.5) return "S";
                if (h < 247.5) return "SW";
                if (h < 292.5) return "W";
                return "NW";
              })()}
            </div>
          )}

          {/* Waypoint Playground toggle — top-left of the map. Clearly labeled
              as a demo to avoid mistaking it for real navigation. */}
          <button onClick={toggleWaypointMode}
                  className={`absolute top-3 left-3 z-[500] rounded-full shadow border px-3 py-1 text-[11px] font-black inline-flex items-center gap-1 transition ${
                    waypointMode
                      ? "bg-amber-500 border-amber-500 text-white"
                      : "bg-white/95 border-stone-200 text-stone-700 hover:bg-white"
                  }`}
                  data-testid="nearby-waypoint-toggle"
                  title={waypointMode ? "Close route planner" : "Plan an A → B route with corridor search"}>
            <Navigation2 size={11}/> {waypointMode ? "Route planner ON" : "Plan route"}
          </button>

          {busy && (
            <div className="absolute top-3 right-3 z-[500] rounded-full bg-white/95 shadow border border-stone-200 px-3 py-1 text-[11px] font-black text-stone-600 inline-flex items-center gap-1" data-testid="nearby-loading">
              <Loader2 size={11} className="animate-spin"/> Loading pins…
            </div>
          )}

          {emptyState && !results.length && (
            <div className="absolute top-12 left-1/2 -translate-x-1/2 z-[500] max-w-[92%] rounded-2xl bg-white/95 shadow border border-stone-200 px-3 py-2 text-[11px] font-bold text-stone-700 flex items-center gap-2" data-testid={`nearby-empty-${emptyState}`}>
              {emptyState === "warming" ? <><Loader2 size={11} className="animate-spin"/> Fetching places for this area from OpenStreetMap…</>
                : emptyState === "upstream" ? <>OpenStreetMap is busy — places for this area aren't available yet.</>
                : emptyState === "offline" ? <>You're offline and nothing is saved for this area.</>
                : <>No places found within this radius.</>}
              {emptyState !== "warming" && (
                <button onClick={() => setReloadTick(t => t + 1)} className="underline text-emerald-700" data-testid="nearby-empty-retry">Retry</button>
              )}
            </div>
          )}

          {/* P2b · POI layer loading + empty-state (bottom, non-blocking) */}
          {layerBusy && activeLayers.length > 0 && (
            <div className="absolute bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[500] rounded-full bg-white/95 shadow border border-stone-200 px-3 py-1.5 text-[11px] font-black text-stone-700 inline-flex items-center gap-1.5"
                 data-testid="map-layer-loading">
              <Loader2 size={12} className="animate-spin"/>
              {t("map.layers.loading", "Loading POIs…")}
            </div>
          )}
          {!layerBusy && layerEmpty && (
            <div className="absolute bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[500] rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-[11px] font-black text-amber-800 inline-flex items-center gap-1.5"
                 data-testid="map-layer-empty">
              <MapPin size={12}/>
              {t("map.layers.empty", "No POIs found in this area")}
            </div>
          )}

          {/* LIVE / CACHED chip — shows on map when data source is known */}
          {!busy && results.length > 0 && (
            <div
              className={`absolute top-3 right-3 z-[500] rounded-full shadow border px-3 py-1 text-[11px] font-black inline-flex items-center gap-1.5 ${
                source === "live"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}
              data-testid={`nearby-source-${source}`}
              title={source === "cached" && cachedAt ? `Cached ${formatAge(cachedAt)}` : "Fresh from network"}
            >
              {source === "live"
                ? <><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"/><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"/></span> LIVE</>
                : <><Wifi size={11} className="opacity-50"/> CACHED · {formatAge(cachedAt)}</>
              }
              <span className="sr-only" data-testid="nearby-tick">{tick}</span>
            </div>
          )}
        </div>

        {/* Offline banner is handled globally in App.js via <OfflineBanner/> —
            no inline duplicate. The LIVE/CACHED chip above still reflects
            data source. */}

        {/* Waypoint Playground bottom sheet — shows the planned stops in order.
            Loudly labeled as PLANNING to avoid confusion with real routing. */}
        {waypointMode && (
          <FloatingSlot zone="bottom-center" order={0} size="banner" zIndex="map-pill" hideOnKeyboard={false}>
            <div className="rounded-2xl bg-slate-900 text-white shadow-2xl border border-amber-500/60 p-3"
                 data-testid="nearby-waypoint-sheet">
            <div className="flex items-center gap-2 mb-2">
              <div className={`rounded-full text-[9px] font-black uppercase px-2 py-0.5 ${routeData ? "bg-emerald-500 text-white" : "bg-amber-500 text-slate-900"}`}
                   data-testid="waypoint-playground-badge">
                {routeData ? `LIVE · ${routeData.route?.provider === "stub" ? "STRAIGHT-LINE" : "OSRM"}` : "PLAN A → B ROUTE"}
              </div>
              <div className="text-[10px] text-white/60 flex-1">
                {routeData
                  ? `${routeData.route?.distance_km} km · ${routeData.route?.duration_min} min · ${routeData.count} POIs in corridor`
                  : "Tap map to add stops · then Search along route"}
              </div>
              <button onClick={() => setWaypointMode(false)} data-testid="waypoint-close"
                      className="text-white/50 hover:text-white p-1"><X size={13}/></button>
            </div>
            {/* Saved offline packs — always visible in the sheet so travellers
                can restore a pre-downloaded route with one tap. */}
            {savedPacks.length > 0 && (
              <div className="mb-2 rounded-2xl bg-slate-800/50 border border-white/10 p-1.5"
                   data-testid="saved-packs-list">
                <div className="text-[9px] uppercase font-black text-sky-200 px-1 mb-1">
                  📥 Offline packs ({savedPacks.length})
                </div>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {savedPacks.slice(0, 6).map((p) => (
                    <div key={p.pack_id} className="flex items-center gap-1.5 rounded-xl bg-slate-900/60 px-2 py-1"
                         data-testid={`saved-pack-${p.pack_id}`}>
                      <button onClick={() => loadPack(p.pack_id)}
                              className="flex-1 text-left text-[10px] font-black text-white truncate hover:text-emerald-300"
                              data-testid="saved-pack-load">
                        {p.name}
                      </button>
                      <span className="text-[8.5px] text-white/40">
                        {(p.pois?.length || 0)} POI · {((p.size_estimate || 0) / 1024).toFixed(0)} KB
                      </span>
                      <button onClick={() => removePack(p.pack_id)}
                              className="text-white/40 hover:text-rose-300 text-[11px]"
                              aria-label="Delete pack"
                              data-testid="saved-pack-delete">×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {waypoints.length === 0 ? (
              <div className="text-[11px] text-white/60 text-center py-3" data-testid="waypoint-empty">
                Drop your first pin on the map — that's <b className="text-sky-300">A</b>. Add up to 3 more for stops and <b className="text-rose-300">B</b>.
              </div>
            ) : (
              <div className="space-y-1" data-testid="waypoint-list">
                {waypoints.map((w, i) => {
                  const isFirst = i === 0;
                  const isLast  = i === waypoints.length - 1 && waypoints.length > 1;
                  const label   = isFirst ? "A" : isLast ? "B" : `S${i}`;
                  const color   = isFirst ? "bg-sky-500" : isLast ? "bg-rose-500" : "bg-amber-500";
                  return (
                    <div key={w.id} className="flex items-center gap-2 rounded-xl bg-white/5 p-2"
                         data-testid={`waypoint-row-${w.id}`}>
                      <div className={`w-6 h-6 rounded-full text-white text-[11px] font-black grid place-items-center shrink-0 ${color}`}>{label}</div>
                      <div className="flex-1 text-[11px] font-mono text-white/80 truncate">
                        {w.lat.toFixed(4)}, {w.lng.toFixed(4)}
                      </div>
                      <button onClick={() => moveWaypoint(i, -1)} disabled={i === 0}
                              className="text-white/50 hover:text-white disabled:opacity-20 text-xs px-1"
                              data-testid={`waypoint-up-${w.id}`}>▲</button>
                      <button onClick={() => moveWaypoint(i, 1)} disabled={i === waypoints.length - 1}
                              className="text-white/50 hover:text-white disabled:opacity-20 text-xs px-1"
                              data-testid={`waypoint-down-${w.id}`}>▼</button>
                      <button onClick={() => removeWaypoint(w.id)}
                              className="text-white/50 hover:text-rose-400 p-1"
                              data-testid={`waypoint-remove-${w.id}`}>
                        <X size={12}/>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {waypoints.length > 0 && (
              <div className="flex gap-2 mt-2">
                <Button onClick={clearWaypoints} data-testid="waypoint-clear"
                        className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[10px] h-7 flex-1">
                  Clear all
                </Button>
                <div className="text-[10px] text-white/50 self-center px-2">
                  {waypoints.length} / 4 pins
                </div>
              </div>
            )}
            {/* Personal Map v2 — real routing corridor controls (iter 55) */}
            {waypoints.length >= 2 && (
              <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid="along-route-panel">
                <div className="text-[10px] uppercase font-black text-emerald-300">Search along route</div>
                <div className="flex flex-wrap gap-1" data-testid="along-cat-chips">
                  {[
                    { id: "washroom",       label: "Washrooms" },
                    { id: "drinking_water", label: "Water" },
                    { id: "cafe",           label: "Cafés" },
                    { id: "restaurant",     label: "Food" },
                    { id: "medical",        label: "Pharmacy" },
                    { id: "hospital",       label: "Hospital" },
                    { id: "parking",        label: "Parking" },
                  ].map(c => {
                    const on = alongCats.includes(c.id);
                    return (
                      <button key={c.id}
                              onClick={() => setAlongCats(list => on ? list.filter(x => x !== c.id) : [...list, c.id].slice(0, 6))}
                              className={`rounded-full text-[10px] font-black px-2.5 py-1 ${on ? "bg-emerald-500 text-slate-900" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                              data-testid={`along-cat-${c.id}`}>
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-white/60 uppercase font-black">Corridor</label>
                  <input type="range" min="0.3" max="3" step="0.1"
                         value={corridorKm} onChange={e => setCorridorKm(parseFloat(e.target.value))}
                         className="flex-1 accent-emerald-400" data-testid="corridor-range"/>
                  <div className="text-[10px] font-mono text-white/80 w-10 text-right" data-testid="corridor-km-label">{corridorKm.toFixed(1)} km</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={searchAlongRoute} disabled={routeBusy || alongCats.length === 0}
                          data-testid="along-route-search-btn"
                          className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[10px] h-8 flex-1 disabled:opacity-40">
                    {routeBusy ? "Routing…" : (routeData ? "Refresh corridor" : `Find ${alongCats.length} category${alongCats.length === 1 ? "" : "s"} along route`)}
                  </Button>
                  {routeData && (
                    <Button onClick={clearRoute} data-testid="along-route-clear-btn"
                            className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[10px] h-8">
                      Clear
                    </Button>
                  )}
                </div>
                {/* Destination-arrival weather — shows the forecast at B for
                    the exact hour the OSRM ETA lands you there. 100% accurate
                    to the endpoint, not the origin. */}
                {destWeather?.arrival && (
                  <div className="rounded-2xl bg-sky-500/10 border border-sky-400/30 p-2 mt-1"
                       data-testid="dest-weather-card">
                    <div className="flex items-center gap-2">
                      <span className="text-[18px]" aria-hidden>
                        {destWeather.arrival.condition?.icon?.startsWith("cloud-rain") ? "🌧️"
                         : destWeather.arrival.condition?.icon === "cloud-showers-heavy" ? "⛈️"
                         : destWeather.arrival.condition?.icon === "sun" ? "☀️"
                         : destWeather.arrival.condition?.icon === "cloud-sun" ? "⛅"
                         : destWeather.arrival.condition?.icon === "bolt" ? "⚡"
                         : destWeather.arrival.condition?.icon === "snowflake" ? "❄️"
                         : "☁️"}
                      </span>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase font-black text-sky-200">On arrival at B</div>
                        <div className="text-[11px] font-black text-white" data-testid="dest-weather-summary">
                          {Math.round(destWeather.arrival.temp_c ?? 0)}°C · {destWeather.arrival.condition?.label}
                          {(destWeather.arrival.precip_prob_pct ?? 0) >= 30 && ` · ${destWeather.arrival.precip_prob_pct}% rain`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] text-white/50 uppercase">ETA</div>
                        <div className="text-[11px] font-black text-white">{Math.round(destWeather.eta_min || 0)} min</div>
                      </div>
                    </div>
                    {destWeather.summary?.will_rain && (
                      <div className="text-[9.5px] text-amber-200 mt-1" data-testid="dest-weather-rain-warn">
                        ⚠️ Rain expected during this trip ({destWeather.summary.rain_hours} h with ≥50% probability). Carry cover.
                      </div>
                    )}
                  </div>
                )}
                {/* Route Weather Timeline strip — rain probability along the
                    corridor, so drivers see which stretch will be wet. */}
                <RouteWeatherStrip timeline={routeTimeline} className="mt-1" />
                {/* Offline Slice 2 — Save this A → B as a pack for offline use */}
                {routeData?.route?.coords?.length >= 2 && (
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-2 mt-2 space-y-1.5"
                       data-testid="save-offline-pack">
                    <div className="text-[10px] uppercase font-black text-emerald-200">
                      📥 Save for offline use
                    </div>
                    <div className="flex gap-1">
                      <input value={packName} onChange={e => setPackName(e.target.value)}
                             placeholder="e.g. Lucknow → Ayodhya"
                             className="flex-1 rounded-full bg-white/10 border border-white/10 text-white text-[10px] px-2.5 py-1"
                             data-testid="pack-name-input"/>
                      <Button onClick={saveCurrentPack} disabled={savingPack}
                              data-testid="pack-save-btn"
                              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[10px] h-7 px-3">
                        {savingPack ? "Saving…" : "Save"}
                      </Button>
                    </div>
                    <div className="text-[9px] text-white/50">
                      Route polyline + {routeData.pois?.length || 0} POIs cached in your browser. Works even when you're offline.
                    </div>
                  </div>
                )}
                {routeData?.pois?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1 pt-1" data-testid="along-route-list">
                    {routeData.pois.slice(0, 12).map((p, i) => (
                      <button key={`arp_${i}`}
                              onClick={() => setDetail({ ...p, name: p.title, title: p.title })}
                              className="w-full flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 p-1.5 text-left"
                              data-testid={`along-route-row-${i}`}>
                        <div className="text-[14px] w-6 text-center">{byId[p.category]?.icon || "•"}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-black text-white truncate">{p.title}</div>
                          <div className="text-[9.5px] text-white/50 truncate">{(p.category || "").replace(/_/g, " ")} · {p.dist_from_route_km} km off · +{p.detour_min}min</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!routeData && (
              <div className="text-[9.5px] text-white/50 mt-2 leading-snug border-t border-white/10 pt-2">
                Real driving distance via OSRM. Corridor search finds POIs within your chosen km of the road path — not a circular GPS ring.
              </div>
            )}
          </div>
          </FloatingSlot>
        )}

        {/* Side list */}
        <div className="border-t md:border-t-0 md:border-l border-stone-200 bg-white overflow-y-auto max-h-[45vh] md:max-h-none" data-testid="nearby-list">
          {grouped && active.size === 0 ? (
            <div className="p-3 space-y-3">
              {Object.entries(grouped).map(([gid, arr]) => {
                const meta = byId[gid] || {};
                return (
                  <div key={gid} data-testid={`nearby-group-${gid}`}>
                    <button onClick={() => setActive(new Set([gid]))}
                            className="w-full flex items-center justify-between text-[11px] uppercase font-black text-stone-500 hover:text-stone-900">
                      <span className="flex items-center gap-1">{meta.icon} {meta.label || gid}</span>
                      <span className="text-stone-400">{arr.length}</span>
                    </button>
                    <div className="mt-1 space-y-1">
                      {arr.slice(0, 3).map((r, i) => (
                        <NearbyRow key={r.id || i} r={r} idx={i} meta={meta} onOpen={() => setDetail({ ...r, ...meta, title: r.name || meta.label })}/>
                      ))}
                    </div>
                  </div>
                );
              })}
              {Object.keys(grouped).length === 0 && (
                <div className="text-center text-sm text-stone-500 py-6">Nothing indexed nearby yet.</div>
              )}
            </div>
          ) : rankedResults.length === 0 ? (
            <div className="text-center text-sm text-stone-500 py-10 px-4">
              {busy ? "Loading…" : "No results. Try another category or move the map."}
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {rankedResults.slice(0, 120).map((r, i) => {
                const meta = byId[r.category] || {};
                return <NearbyRow key={r.id || i} r={r} idx={i} meta={meta} onOpen={() => setDetail({ ...r, ...meta, title: r.name || meta.label })}/>;
              })}
            </div>
          )}
        </div>
      </div>

      {/* Category drawer */}
      {drawer && (
        <div className="fixed inset-0 z-[600] bg-black/40" onClick={() => setDrawer(false)} data-testid="nearby-drawer">
          <div onClick={(e) => e.stopPropagation()} className="fixed inset-x-0 bottom-0 md:inset-y-0 md:right-0 md:left-auto md:w-96 bg-white rounded-t-3xl md:rounded-t-none md:rounded-l-3xl p-4 max-h-[85vh] md:max-h-none overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-stone-900 text-sm">Discover categories</h3>
              <button onClick={() => setDrawer(false)} className="text-stone-500"><X size={16}/></button>
            </div>
            <div className="mt-3 space-y-4">
              {catalog.map((g) => (
                <div key={g.group} data-testid={`nearby-drawer-group-${g.group.replace(/\s|&/g, "-")}`}>
                  <div className="text-[10px] uppercase font-black text-stone-500 mb-1">{g.group}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.items.map(item => {
                      const on = active.has(item.id);
                      return (
                        <button key={item.id} onClick={() => toggleCat(item.id)}
                                className={`inline-flex items-center gap-1 rounded-full text-[11px] font-black px-3 py-1.5 border ${on ? "text-white border-transparent" : "text-stone-700 bg-stone-50 border-stone-200"}`}
                                style={on ? { backgroundColor: item.color } : {}}
                                data-testid={`nearby-drawer-item-${item.id}`}>
                          <span>{item.icon}</span> {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={() => setDrawer(false)}
                    className="mt-4 w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[12px]" data-testid="nearby-drawer-close">
              Show {active.size} on map
            </Button>
          </div>
        </div>
      )}

      {/* Place detail sheet */}
      {detail && (() => {
        const cfg = getCategoryConfig(detail.category);
        const CatIcon = cfg.Icon;
        const phone = detail.phone || detail.raw?.phone;
        const website = detail.website || detail.raw?.website;
        const label = cfg.tKey ? t(cfg.tKey, cfg.label) : cfg.label;
        return (
        <div className="fixed inset-0 z-[700] bg-black/40 flex items-end md:items-center justify-center p-2 md:p-4" onClick={() => setDetail(null)} data-testid="nearby-detail-sheet">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl grid place-items-center text-white flex-none"
                   style={{ backgroundColor: detail.color || cfg.color || "#0f766e" }}>
                <CatIcon size={20} aria-hidden/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase text-stone-500 font-black">{label}</div>
                <div className="font-black text-stone-900 text-sm break-words" data-testid="nearby-detail-title">{detail.title || detail.popup || "Place"}</div>
                {detail.raw?.address && <div className="text-[11px] text-stone-500 mt-0.5">{detail.raw.address}</div>}
                {typeof detail.distance_km === "number" && <div className="text-[11px] text-emerald-700 font-black mt-0.5">{detail.distance_km.toFixed(1)} km {t("common.away", "away")}</div>}
                {detail.source && (
                  <div className="text-[10px] text-stone-400 mt-1" data-testid="nearby-detail-source">
                    {detail.source === "listings" ? t("poi.source.listings", "Find2Near listing") : t("poi.source.osm", "OpenStreetMap")}
                  </div>
                )}
              </div>
              <button onClick={() => setDetail(null)} aria-label={t("common.close", "Close")} className="text-stone-500"><X size={14}/></button>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${detail.lat},${detail.lng}`} target="_blank" rel="noreferrer"
                 className="flex-1 min-w-[120px] rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] py-2 text-center inline-flex items-center justify-center gap-1"
                 data-testid="nearby-detail-directions">
                <MapPin size={12}/> {t("poi.action.directions", "Directions")}
              </a>
              {phone && (
                <a href={`tel:${phone}`}
                   className="flex-1 min-w-[120px] rounded-full bg-sky-600 hover:bg-sky-700 text-white font-black text-[11px] py-2 text-center inline-flex items-center justify-center gap-1"
                   data-testid="nearby-detail-call">
                  📞 {t("poi.action.call", "Call")}
                </a>
              )}
              {website && (
                <a href={website} target="_blank" rel="noreferrer"
                   className="flex-1 min-w-[120px] rounded-full bg-stone-100 text-stone-700 font-black text-[11px] py-2 text-center"
                   data-testid="nearby-detail-website">
                  {t("poi.action.view", "View")}
                </a>
              )}
              <button onClick={() => setDetail(null)}
                      className="flex-1 min-w-[80px] rounded-full bg-stone-100 text-stone-700 font-black text-[11px] py-2">
                {t("common.close", "Close")}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function NearbyRow({ r, idx, meta, onOpen }) {
  const lat = r.lat ?? r.latitude, lng = r.lng ?? r.longitude;
  const title = r.title || r.name || r.category || meta?.label || "Place";
  const dist = typeof r.distance_km === "number" ? `${r.distance_km.toFixed(1)} km` : null;
  return (
    <button onClick={onOpen} className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-stone-50" data-testid={`nearby-row-${r.id || idx}`}>
      <div className="w-7 h-7 rounded-lg text-white grid place-items-center text-sm flex-none"
           style={{ backgroundColor: meta?.color || "#0f766e" }}>{meta?.icon || "•"}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-black text-stone-900 truncate">{title}</div>
        <div className="text-[11px] text-stone-500 truncate">
          {(r.category || meta?.label || "").replace(/_/g, " ")}{r.category && dist && " · "}{dist}
        </div>
      </div>
      {lat && lng && (
        <span className="text-[10px] font-black text-emerald-700 inline-flex items-center gap-0.5"><MapPin size={10}/> Open</span>
      )}
    </button>
  );
}
